/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Group
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new group in the organization.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useModifyOrgGHGroupMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import FTModal from "../../../components/common/FTModal";
import { useGHContext } from "./contexts/groupHierarchy.context";
import HierachyBreadcrumbTag from "./hierarchyBreadcrumbTag";

type TModalConfirmMoveGroupProps = {
};

const ModalConfirmMoveGroup = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.groups.hierarchy.confirm_move' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { movingGroup, setMovingGroup, moveToGroup, setMoveToGroup, setParentOfMovingGroup } = useGHContext();

  const [showHierarchy, setShowHierarchy] = React.useState(false);

  const [modifyOrgGHGroupAPITrigger, { isLoading }] = useModifyOrgGHGroupMutation();

  const hideModal = () => {
    if (isLoading) return;
    dispatch(setModalsData({ ...modalsState, showConfirmMoveGroup: false }));
    setMovingGroup(false);
    setMoveToGroup(null);
    setParentOfMovingGroup(null);
    setShowHierarchy(false);
  };

  const onConfirm = () => {
    if (!moveToGroup || !movingGroup) {
      console.log('Please make sure to select the group to move to');
    }
    modifyOrgGHGroupAPITrigger({
      parent_group: moveToGroup?.toString() || "",
      groupid: movingGroup?.toString() || "",
    }).then(() => {
      toast.success(t('toast.move_success'));
      hideModal();
    }).catch((error) => {
      toast.error(serializeErrorKeyValues(error));
    });
  }

  return (
    <FTModal
      visible={modalsState.showConfirmMoveGroup}
      heading={t("heading")}
      containerStyle="max-w-[calc(100vw-4rem)] w-[560px]"
      onHide={hideModal}
    >
      <div className="grid grid-cols-6 gap-6">

        <div className="col-span-6 flex text-sm leading-6 mt-10">
          <div className="flex flex-col flex-1">
            <span className="font-semibold">{t('group_caption_left')}</span>
            <span className="text-heading-gray888">{movingGroup}</span>
          </div>
          <div className="flex items-center justify-center w-24">
            {t('to')}
          </div>
          <div className="flex flex-col flex-1">
            <span className="font-semibold">{t('group_caption_right')}</span>
            <span className="text-heading-gray888">{moveToGroup}</span>
            <button
              className="font-semibold text-accent-blue-dark text-xs w-auto self-start mt-1"
              type="button"
              onClick={() => setShowHierarchy(!showHierarchy)}
            >
              {t('view_hierarchy')}
            </button>
          </div>
        </div>

        {moveToGroup && showHierarchy && (
          <div className="col-span-6 flex flex-wrap text-sm font-semibold text-heading-gray888">
            <HierachyBreadcrumbTag
              group_id={moveToGroup}
              lastGroup={true}
            />
          </div>
        )}

        <AdminFormFieldSubmit
          label={tMain('confirm')}
          type="button"
          variant="primary"
          onClick={onConfirm}
          disabled={isLoading}
        />

        <AdminFormFieldSubmit
          label={tMain('cancel')}
          type="button"
          variant="danger-transparent"
          onClick={hideModal}
          disabled={isLoading}
        />
      </div>
    </FTModal>
  )
};
export default ModalConfirmMoveGroup;